import { faSync } from '@fortawesome/pro-light-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Alert,
  Badge,
  Card,
  cn,
  Label,
  Modal,
  MultiSelect,
  MultiSelectOption,
  Page,
  PasswordInput,
  SupportingText,
  SwitchInput,
  TextInput,
  UrlInput,
} from 'corso-admin-ui';
import {
  CrewMerchantUi,
  EspMetric,
  isTruthy,
  klaviyoEspMetricName,
  ShippingPlusConfig,
  ShopifyAppSubscriptionStatusEnum,
} from 'corso-types';
import {
  ChangeEventHandler,
  ComponentProps,
  FormEventHandler,
  PropsWithChildren,
  ReactNode,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import ClipboardButton from '~/components/ClipboardButton';
import InfoPopover from '~/components/InfoPopover';
import Separator from '~/components/Separator';
import SettingsAnchorCard from '~/features/settings/components/SettingsAnchorCard';
import { useShopifyPlan } from '~/features/settings/hooks/useShopifyPlan';
import { KlaviyoTestEventForm } from '~/features/settings/integrations/KlaviyoTestEventForm';
import {
  useConfigSettings,
  useConfigSettingsUpdate,
} from '~/hooks/useConfigSettings';
import {
  useIntegrationSettingsData,
  useIntegrationSettingsUpdate,
  useKlaviyoDisconnect,
  useShippingProviderRefresh,
  useStripeDisconnect,
} from '~/hooks/useIntegrationSettings';
import { useToast } from '~/hooks/useToast';
import { useIsCurrentZendesk } from '~/integrations/zendesk/useIsCurrentZendesk';
import { useZendeskParams } from '~/integrations/zendesk/useZendeskParams';
import { IntegrationSettings } from '~/types';

const returnWarrantyProtect = 'Built for Returns, Warranties & Shipping Plus';

const returnWarranty = 'Built for Returns & Warranties';

const builtForShippingProtection = 'Built for Shipping Plus';

const REPLACE_SECRET_HINT = 'Enter a new value to replace the saved one.';

// a stored credential comes back masked; leaving the field blank keeps it, so entry
// is only required when there is nothing stored yet. `details` takes the field's own
// copy so a static blurb can't silently clobber the hint.
const secretInputProps = (
  maskedSecret: string | null | undefined,
  details?: string,
) => ({
  required: !maskedSecret,
  placeholder: maskedSecret ?? undefined,
  details:
    [details, maskedSecret ? REPLACE_SECRET_HINT : undefined]
      .filter(isTruthy)
      .join(' ') || undefined,
});

function IntegrationSection({
  children,
  title,
  id,
}: PropsWithChildren<{
  title: string;
  id: ComponentProps<typeof SettingsAnchorCard>['id'];
}>) {
  return (
    <SettingsAnchorCard id={id}>
      <Card.Heading as="h3">{title}</Card.Heading>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">{children}</div>
    </SettingsAnchorCard>
  );
}

function IntegrationCard({
  id,
  imgUrl,
  title,
  description,
  action,
  worksWith,
}: {
  id?: string;
  imgUrl: string;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  worksWith?: string;
}) {
  return (
    <Card id={id}>
      <div className="sr-only">
        <Card.Heading as="h4">{title}</Card.Heading>
      </div>
      <div className="flex items-center justify-between gap-1">
        <div className="flex items-center gap-1">
          <img src={imgUrl} alt={title} className="h-10 w-auto" aria-hidden />
          {worksWith && (
            <InfoPopover title="Works With">
              <InfoPopover.Prose>{worksWith}</InfoPopover.Prose>
            </InfoPopover>
          )}
        </div>
        {action}
      </div>
      {description && (
        <>
          <Separator />
          <SupportingText>{description}</SupportingText>
        </>
      )}
    </Card>
  );
}

function AppSubscription() {
  const { data } = useIntegrationSettingsData();

  const shopifyPlan = useShopifyPlan();

  const {
    appSubscriptionStatus = ShopifyAppSubscriptionStatusEnum.pending,
    appSubscriptionConfirmationUrl,
  } = data?.appSubscription ?? {};
  const content = data?.appSubscriptionStatusContent;

  return (
    <div className="col-span-full">
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/shopify-1.png"
        title="Shopify Status"
        worksWith={`Shopify Plan: ${shopifyPlan}`}
        description={content?.supportingText}
        action={
          content?.needsAction && appSubscriptionConfirmationUrl ? (
            <Action
              size="lg"
              variant="primary"
              to={appSubscriptionConfirmationUrl}
            >
              Approve
            </Action>
          ) : (
            <Badge
              variant={content?.badgeVariant}
              className={cn(
                'w-28 justify-center px-3 py-2 capitalize sm:w-40',
                content?.supportingText && 'row-span-2 place-self-center',
              )}
            >
              {appSubscriptionStatus}
            </Badge>
          )
        }
      />
    </div>
  );
}

function ShipHeroForm({
  showForm,
  closeForm,
  values,
}: {
  showForm: boolean;
  closeForm: () => void;
  values: IntegrationSettings['shipHeroConfig'] | undefined;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    register,
    reset,
    control,
  } = useForm<CrewMerchantUi.ShipHeroConfigForm>({
    resolver: zodResolver(CrewMerchantUi.shipHeroConfigFormSchema),
    values: {
      refreshToken: '',
      // ShipHero connections have always created RMAs, so a new one defaults to on
      shouldCreateRma: values?.shouldCreateRma ?? true,
    },
  });

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ refreshToken, ...config }) => {
      const connectedUpdate = refreshToken
        ? ({ action: 'rotateSecret', refreshToken, ...config } as const)
        : ({ action: 'configure', ...config } as const);

      return saveChanges({
        kind: 'shipHeroConfig',
        shipHeroConfig: values
          ? // connected: a value rotates the secret, blank keeps the stored one
            connectedUpdate
          : { action: 'connect', refreshToken, ...config },
      })
        .then(closeHandler)
        .catch(console.error);
    }, console.error)(event).catch(console.error);
  };

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="ShipHero"
      description="Configure your ShipHero account."
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Controller
          control={control}
          name="shouldCreateRma"
          render={({ field: { value, onChange } }) => (
            <SwitchInput
              id="shiphero-create-rma"
              label="Create RMA"
              defaultChecked={value}
              onChange={onChange}
            />
          )}
        />

        <PasswordInput
          id="shiphero-refresh-token"
          label="Refresh Token"
          {...secretInputProps(values?.maskedRefreshToken)}
          {...register('refreshToken')}
          error={errors.refreshToken?.message}
        />

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function ShipHero() {
  const { data } = useIntegrationSettingsData();
  const { mutate: saveChanges } = useIntegrationSettingsUpdate();
  const { shipHeroConfig } = data ?? {};
  const [showForm, setShowForm] = useState(false);

  const shipHeroEnabled = !!shipHeroConfig;

  const disconnectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;

    if (!checked && shipHeroConfig) {
      saveChanges({
        kind: 'shipHeroConfig',
        shipHeroConfig: { action: 'disconnect' },
      });
    } else {
      setShowForm(true);
    }
  };

  return (
    <>
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/shiphero.png"
        title="ShipHero"
        worksWith={returnWarranty}
        description={
          shipHeroEnabled
            ? 'ShipHero is connected.'
            : 'Connect a ShipHero account to create RMA requests.'
        }
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="shiphero-switch"
              label="Connect or disconnect ShipHero account"
              labelVisuallyHidden
              checked={shipHeroEnabled}
              onChange={disconnectHandler}
            />
            {shipHeroEnabled && (
              <Action
                icon="settings"
                onClick={() => setShowForm(true)}
                accessibilityLabel="Edit ShipHero Configuration"
              />
            )}
          </div>
        }
      />
      <ShipHeroForm
        showForm={showForm}
        values={shipHeroConfig}
        closeForm={() => setShowForm(false)}
      />
    </>
  );
}

function ShipBobForm({
  showForm,
  closeForm,
  values,
}: {
  showForm: boolean;
  closeForm: () => void;
  values: IntegrationSettings['shipBobConfig'] | undefined;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    register,
    reset,

    control,
  } = useForm<CrewMerchantUi.ShipBobConfigForm>({
    resolver: zodResolver(CrewMerchantUi.shipBobConfigFormSchema),
    values: {
      accessToken: '',
      shouldCreateRma: values?.shouldCreateRma ?? false,
    },
  });

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ accessToken, ...config }) => {
      const connectedUpdate = accessToken
        ? ({ action: 'rotateSecret', accessToken, ...config } as const)
        : ({ action: 'configure', ...config } as const);

      return saveChanges({
        kind: 'shipBobConfig',
        shipBobConfig: values
          ? // connected: a value rotates the secret, blank keeps the stored one
            connectedUpdate
          : { action: 'connect', accessToken, ...config },
      })
        .then(closeHandler)
        .catch(console.error);
    }, console.error)(event).catch(console.error);
  };

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="ShipBob"
      description="Configure your ShipBob account."
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Controller
          control={control}
          name="shouldCreateRma"
          render={({ field: { value, onChange } }) => (
            <SwitchInput
              id="shipbob-create-rma-2"
              label="Create RMA"
              details="Create an RMA request at the original fulfillment center when a return is marked as In-Transit."
              defaultChecked={value}
              onChange={onChange}
            />
          )}
        />

        <PasswordInput
          id="shipbob-access-token"
          label="Access Token"
          {...secretInputProps(values?.maskedAccessToken)}
          {...register('accessToken')}
          error={errors.accessToken?.message}
        />

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function ShipBob() {
  const { data } = useIntegrationSettingsData();
  const { mutate: saveChanges } = useIntegrationSettingsUpdate();
  const { shipBobConfig } = data ?? {};
  const [showForm, setShowForm] = useState(false);

  const shipBobEnabled = !!shipBobConfig;

  const disconnectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;

    if (!checked && shipBobEnabled) {
      saveChanges({
        kind: 'shipBobConfig',
        shipBobConfig: { action: 'disconnect' },
      });
    } else {
      setShowForm(true);
    }
  };

  return (
    <>
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/shipbob-3.png"
        title="ShipBob"
        worksWith={returnWarranty}
        description={
          shipBobConfig
            ? 'ShipBob is connected.'
            : 'Connect a ShipBob account to create RMA requests.'
        }
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="shipBob-switch"
              label="Connect or disconnect ShipBob account"
              labelVisuallyHidden
              checked={shipBobEnabled}
              onChange={disconnectHandler}
            />
            {shipBobEnabled && (
              <Action
                icon="settings"
                onClick={() => setShowForm(true)}
                accessibilityLabel="Edit ShipBob Configuration"
              />
            )}
          </div>
        }
      />
      <ShipBobForm
        showForm={showForm}
        values={shipBobConfig}
        closeForm={() => setShowForm(false)}
      />
    </>
  );
}

function BlueBoxForm({
  showForm,
  closeForm,
  values,
}: {
  showForm: boolean;
  closeForm: () => void;
  values: IntegrationSettings['blueBoxConfig'] | undefined;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    register,
    reset,

    control,
  } = useForm<CrewMerchantUi.BlueBoxConfigForm>({
    resolver: zodResolver(CrewMerchantUi.blueBoxConfigFormSchema),
    values: {
      password: '',
      username: values?.username ?? '',
      shouldCreateRma: values?.shouldCreateRma ?? false,
    },
  });

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ password, ...config }) => {
      const connectedUpdate = password
        ? ({ action: 'rotateSecret', password, ...config } as const)
        : ({ action: 'configure', ...config } as const);

      return saveChanges({
        kind: 'blueBoxConfig',
        blueBoxConfig: values
          ? // connected: a value rotates the secret, blank keeps the stored one
            connectedUpdate
          : { action: 'connect', password, ...config },
      })
        .then(closeHandler)
        .catch(console.error);
    }, console.error)(event).catch(console.error);
  };

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="BlueBox"
      description="Configure your BlueBox account."
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Controller
          control={control}
          name="shouldCreateRma"
          render={({ field: { value, onChange } }) => (
            <SwitchInput
              id="blue-box-create-rma"
              label="Create RMA"
              details="Create an RMA request when a return is marked as In-Transit."
              defaultChecked={value}
              onChange={onChange}
            />
          )}
        />

        <TextInput
          id="blue-box-username"
          label="Username"
          required
          {...register('username')}
          error={errors.username?.message}
        />

        <PasswordInput
          id="blue-box-password"
          label="Password"
          {...secretInputProps(values?.maskedPassword)}
          {...register('password')}
          error={errors.password?.message}
        />

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function BlueBox() {
  const { data } = useIntegrationSettingsData();
  const { mutate: saveChanges } = useIntegrationSettingsUpdate();
  const { blueBoxConfig } = data ?? {};
  const [showForm, setShowForm] = useState(false);

  const blueBoxEnabled = !!blueBoxConfig;

  const disconnectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;

    if (!checked && blueBoxEnabled) {
      saveChanges({
        kind: 'blueBoxConfig',
        blueBoxConfig: { action: 'disconnect' },
      });
    } else {
      setShowForm(true);
    }
  };

  return (
    <>
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/idf.png"
        title="BlueBox"
        worksWith={returnWarranty}
        description={
          blueBoxConfig
            ? 'BlueBox is connected.'
            : 'Connect a BlueBox account to create RMA requests.'
        }
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="blueBox-switch"
              label="Connect or disconnect BlueBox account"
              labelVisuallyHidden
              checked={blueBoxEnabled}
              onChange={disconnectHandler}
            />
            {blueBoxEnabled && (
              <Action
                icon="settings"
                onClick={() => setShowForm(true)}
                accessibilityLabel="Edit BlueBox Configuration"
              />
            )}
          </div>
        }
      />
      <BlueBoxForm
        showForm={showForm}
        values={blueBoxConfig}
        closeForm={() => setShowForm(false)}
      />
    </>
  );
}

function MasonHubForm({
  showForm,
  closeForm,
  values,
}: {
  showForm: boolean;
  closeForm: () => void;
  values: IntegrationSettings['masonHubConfig'] | undefined;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    register,
    reset,
    control,
  } = useForm<CrewMerchantUi.MasonHubConfigForm>({
    resolver: zodResolver(CrewMerchantUi.masonHubConfigFormSchema),
    values: {
      apiToken: '',
      urlSlug: values?.urlSlug ?? '',
      shouldCreateRma: values?.shouldCreateRma ?? true,
    },
  });

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ apiToken, ...config }) => {
      const connectedUpdate = apiToken
        ? ({ action: 'rotateSecret', apiToken, ...config } as const)
        : ({ action: 'configure', ...config } as const);

      return saveChanges({
        kind: 'masonHubConfig',
        masonHubConfig: values
          ? // connected: a value rotates the secret, blank keeps the stored one
            connectedUpdate
          : { action: 'connect', apiToken, ...config },
      })
        .then(closeHandler)
        .catch(console.error);
    }, console.error)(event).catch(console.error);
  };

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="MasonHub"
      description="Configure your MasonHub account."
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Controller
          control={control}
          name="shouldCreateRma"
          render={({ field: { value, onChange } }) => (
            <SwitchInput
              id="mason-hub-create-rma"
              label="Create RMA"
              details="Create an RMA in MasonHub when a return is marked as In-Transit."
              defaultChecked={value}
              onChange={onChange}
            />
          )}
        />

        <TextInput
          id="mason-hub-url-slug"
          label="Account Slug"
          details="The account name in your MasonHub API URL, provided by your MasonHub representative."
          required
          {...register('urlSlug')}
          error={errors.urlSlug?.message}
        />

        <PasswordInput
          id="mason-hub-api-token"
          label="API Token"
          {...secretInputProps(values?.maskedApiToken)}
          {...register('apiToken')}
          error={errors.apiToken?.message}
        />

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function MasonHub() {
  const { data } = useIntegrationSettingsData();
  const { mutate: saveChanges } = useIntegrationSettingsUpdate();
  const { masonHubConfig } = data ?? {};
  const [showForm, setShowForm] = useState(false);

  const masonHubEnabled = !!masonHubConfig;

  const disconnectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;

    if (!checked && masonHubEnabled) {
      saveChanges({
        kind: 'masonHubConfig',
        masonHubConfig: { action: 'disconnect' },
      });
    } else {
      setShowForm(true);
    }
  };

  return (
    <>
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/masonhub.png"
        title="MasonHub"
        worksWith={returnWarranty}
        description={
          masonHubConfig
            ? 'MasonHub is connected.'
            : 'Connect a MasonHub account to create RMAs and receive inspection results.'
        }
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="masonHub-switch"
              label="Connect or disconnect MasonHub account"
              labelVisuallyHidden
              checked={masonHubEnabled}
              onChange={disconnectHandler}
            />
            {masonHubEnabled && (
              <Action
                icon="settings"
                onClick={() => setShowForm(true)}
                accessibilityLabel="Edit MasonHub Configuration"
              />
            )}
          </div>
        }
      />
      <MasonHubForm
        showForm={showForm}
        values={masonHubConfig}
        closeForm={() => setShowForm(false)}
      />
    </>
  );
}

function SendlaneForm({
  showForm,
  closeForm,
  values,
}: {
  showForm: boolean;
  closeForm: () => void;
  values: IntegrationSettings['sendLaneConfig'] | undefined;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    register,
    reset,
  } = useForm<CrewMerchantUi.SendLaneConfigForm>({
    resolver: zodResolver(CrewMerchantUi.sendLaneConfigFormSchema),
    values: { accessToken: '' },
  });

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(
      ({ accessToken }) =>
        saveChanges({
          kind: 'sendLaneConfig',
          sendLaneConfig: {
            action: values ? 'rotateSecret' : 'connect',
            accessToken,
          },
        })
          .then(closeHandler)
          .catch(console.error),
      console.error,
    )(event).catch(console.error);
  };

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="Sendlane"
      description="Configure your Sendlane account."
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <PasswordInput
          id="sendlane-access-token"
          label="Access Token"
          {...secretInputProps(values?.maskedAccessToken)}
          // the token is Sendlane's only field, so there is nothing to save without it
          required
          {...register('accessToken')}
          error={errors.accessToken?.message}
        />

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function SendlaneConfig() {
  const { data } = useIntegrationSettingsData();
  const { mutate: saveChanges } = useIntegrationSettingsUpdate();
  const { sendLaneConfig } = data ?? {};
  const [showForm, setShowForm] = useState(false);

  const isSendlaneEnabled = !!sendLaneConfig;

  const disconnectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;

    if (!checked && isSendlaneEnabled) {
      saveChanges({
        kind: 'sendLaneConfig',
        sendLaneConfig: { action: 'disconnect' },
      });
    } else {
      setShowForm(true);
    }
  };

  const connectedText = isSendlaneEnabled
    ? 'Sendlane is connected.'
    : 'Connect a Sendlane account.';

  return (
    <>
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/sendlane.png"
        title="Sendlane"
        worksWith={returnWarranty}
        description={
          <div>
            <p>
              {connectedText}{' '}
              <Action
                variant="link"
                to="https://help.corso.com/sendlane-integration"
                target="_blank"
                rel="noreferrer"
              >
                Learn More
              </Action>
            </p>
          </div>
        }
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="sendlane-switch"
              label="Connect or disconnect Sendlane account"
              labelVisuallyHidden
              checked={isSendlaneEnabled}
              onChange={disconnectHandler}
            />
            {isSendlaneEnabled && (
              <Action
                icon="settings"
                onClick={() => setShowForm(true)}
                accessibilityLabel="Edit Sendlane Configuration"
              />
            )}
          </div>
        }
      />
      <SendlaneForm
        showForm={showForm}
        values={sendLaneConfig}
        closeForm={() => setShowForm(false)}
      />
    </>
  );
}

function StripeConfig() {
  const { data: { stripeConfig } = {} } = useIntegrationSettingsData();
  const { mutate: disconnectStripe, isPending } = useStripeDisconnect();

  if (!stripeConfig) return null;

  const { connected } = stripeConfig;
  const changeHandler = () =>
    connected
      ? disconnectStripe()
      : window.open(stripeConfig.startOauthUrl, '_blank');

  return (
    <IntegrationCard
      imgUrl="https://cdn.corso.com/img/stripe.png"
      title="Stripe"
      worksWith={returnWarranty}
      description={
        connected
          ? 'Stripe is connected.'
          : 'Connect a Stripe account to collect additional funds from customers when necessary.'
      }
      action={
        <SwitchInput
          id="stripe-switch"
          label="Connect or disconnect Stripe account"
          labelVisuallyHidden
          checked={connected}
          disabled={isPending}
          onChange={changeHandler}
        />
      }
    />
  );
}

function KlaviyoKnowledgeBase() {
  return (
    <Action
      variant="link"
      to="https://help.corso.com/klaviyo-integration"
      target="_blank"
      rel="noreferrer"
    >
      Learn More
    </Action>
  );
}

type KlaviyoConfigUpdate = Extract<
  CrewMerchantUi.SettingsIntegrationUpdate,
  {
    kind: 'klaviyoConfig';
  }
>;

type ConnectKlaviyoConfig = Extract<
  IntegrationSettings['klaviyoConfig'],
  {
    connected: true;
  }
>;
function KlaviyoForm({
  showForm,
  closeForm,
  values,
}: {
  showForm: boolean;
  closeForm: () => void;
  values: ConnectKlaviyoConfig | undefined;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const { mutate: disconnectKlaviyo, isPending } = useKlaviyoDisconnect();
  const toast = useToast();
  const [showTestForm, setShowTestForm] = useState(false);
  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },

    watch,
    reset,
    control,
  } = useForm<KlaviyoConfigUpdate>({
    resolver: zodResolver(CrewMerchantUi.klaviyoConfigUpdateSchema),
    values: {
      kind: 'klaviyoConfig',
      klaviyoConfig: values ?? {
        isEnabled: false,
        isOrderTrackingEnabled: false,
      },
    },
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(
      (updates) =>
        saveChanges(updates)
          .then(() => closeForm())
          .catch(console.error),
      console.error,
    )(event).catch(console.error);
  };

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const disconnectHandler = () => {
    if (!values?.disconnectOauthUrl) return;
    disconnectKlaviyo(values.disconnectOauthUrl);
    saveChanges({
      kind: 'klaviyoConfig',
      klaviyoConfig: { isEnabled: false, isOrderTrackingEnabled: false },
    })
      .then(() => {
        toast.show('Klaviyo disconnected successfully...Reloading page.');
        closeForm();
        reset();
        // gross hack :(
        window.location.reload();
      })
      .catch((error) => console.error(error));
  };

  if (!values?.connected) return null;

  const isOrderTrackingEnabled = watch('klaviyoConfig.isOrderTrackingEnabled');

  const events = Object.values(EspMetric)
    .filter(
      (metric) =>
        isOrderTrackingEnabled || metric !== EspMetric.orderShipmentUpdate,
    )
    .map((metric) => klaviyoEspMetricName[metric]);

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="Klaviyo"
      description="Configure your Klaviyo account."
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Controller
          control={control}
          name="klaviyoConfig.isOrderTrackingEnabled"
          render={({ field: { value, onChange } }) => (
            <SwitchInput
              id="klaviyo-is-order-tracking-enabled"
              details="When enabled, Corso will send order tracking events to Klaviyo. Separate from the Tracking setting, which controls whether tracking is shown in the customer portal."
              label="Order Tracking Events"
              defaultChecked={value}
              onChange={onChange}
              error={
                'klaviyoConfig' in errors
                  ? errors.klaviyoConfig?.isOrderTrackingEnabled?.message
                  : undefined
              }
            />
          )}
        />

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <Label>Corso Events</Label>
            <Action onClick={() => setShowTestForm(true)}>Test</Action>
          </div>

          {events.map((event) => (
            <div className="flex items-center gap-2" key={event}>
              <ClipboardButton onClick={() => event} variant="ghost" />
              <Badge>{event}</Badge>
            </div>
          ))}
        </div>

        {values.disconnectOauthUrl && (
          <>
            <div>
              <Label>Disconnect Klaviyo</Label>
              <p className="text-xs text-neutral-500">
                Disconnecting will permanently unlink your Klaviyo account from
                Corso. This action will remove all synced events and uninstall
                the Klaviyo app.
              </p>
            </div>
            <Action
              loading={isPending}
              onClick={disconnectHandler}
              accessibilityLabel="Disconnect Klaviyo Integration"
            >
              Disconnect
            </Action>
          </>
        )}

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>

      <KlaviyoTestEventForm
        showForm={showTestForm}
        closeForm={() => setShowTestForm(false)}
        isOrderTrackingEnabled={!!isOrderTrackingEnabled}
      />
    </Modal>
  );
}

function KlaviyoConfig() {
  const { data: { klaviyoConfig } = {} } = useIntegrationSettingsData();
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const [showForm, setShowForm] = useState(false);

  if (!klaviyoConfig) return null;

  const { connected } = klaviyoConfig;
  const isEnabled = connected && klaviyoConfig.isEnabled;

  const connectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;
    if (!connected) {
      window.open(klaviyoConfig.startOauthUrl, '_blank');
    } else {
      saveChanges({
        kind: 'klaviyoConfig',
        klaviyoConfig: {
          isEnabled: checked,
          isOrderTrackingEnabled: klaviyoConfig.isOrderTrackingEnabled,
        },
      }).catch(console.error);
    }
  };

  const description =
    // oxlint-disable-next-line no-nested-ternary
    connected ? (
      isEnabled ? (
        'Klaviyo is enabled.'
      ) : (
        'Klaviyo is disabled.'
      )
    ) : (
      <div>
        <p>
          Connect a Klaviyo account. <KlaviyoKnowledgeBase />
        </p>
      </div>
    );

  return (
    <>
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/klaviyo.png"
        title="Klaviyo"
        worksWith={returnWarranty}
        description={description}
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="klaviyo-switch"
              label="Enable or disable Klaviyo integration"
              labelVisuallyHidden
              checked={isEnabled}
              onChange={connectHandler}
            />
            {connected && (
              <Action
                icon="settings"
                onClick={() => setShowForm(true)}
                accessibilityLabel="Edit Klaviyo Configuration"
              />
            )}
          </div>
        }
      />
      {connected && (
        <KlaviyoForm
          showForm={showForm}
          values={klaviyoConfig}
          closeForm={() => setShowForm(false)}
        />
      )}
    </>
  );
}

function AttentiveConfig() {
  return (
    <IntegrationCard
      imgUrl="https://cdn.corso.com/img/attentive-1.png"
      title="Attentive"
      worksWith={returnWarranty}
      description="Coming soon!"
      action={
        <SwitchInput
          id="attentive-switch"
          label="Connect or disconnect Attentive"
          labelVisuallyHidden
          checked={false}
          disabled
        />
      }
    />
  );
}

function XbFulfillmentForm({
  showForm,
  closeForm,
  values,
}: {
  showForm: boolean;
  closeForm: () => void;
  values: IntegrationSettings['xbFulfillmentConfig'] | undefined;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    register,
    reset,
    control,
  } = useForm<CrewMerchantUi.XbFulfillmentConfigForm>({
    resolver: zodResolver(CrewMerchantUi.xbFulfillmentConfigFormSchema),
    values: {
      password: '',
      username: values?.username ?? '',
      shouldCreateRma: values?.shouldCreateRma ?? false,
      endpoint: values?.endpoint ?? '',
    },
  });

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ password, ...config }) => {
      const connectedUpdate = password
        ? ({ action: 'rotateSecret', password, ...config } as const)
        : ({ action: 'configure', ...config } as const);

      return saveChanges({
        kind: 'xbFulfillmentConfig',
        xbFulfillmentConfig: values
          ? // connected: a value rotates the secret, blank keeps the stored one
            connectedUpdate
          : { action: 'connect', password, ...config },
      })
        .then(closeHandler)
        .catch(console.error);
    }, console.error)(event).catch(console.error);
  };

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="XB Fulfillment"
      description="Configure your XB Fulfillment account."
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="xb-fulfillment-endpoint"
          label="Endpoint"
          required
          {...register('endpoint')}
          error={errors.endpoint?.message}
        />

        <TextInput
          id="xb-fulfillment-username"
          label="Username"
          required
          {...register('username')}
          error={errors.username?.message}
        />

        <PasswordInput
          id="xb-fulfillment-password"
          label="Password"
          {...secretInputProps(values?.maskedPassword)}
          {...register('password')}
          error={errors.password?.message}
        />

        <Controller
          control={control}
          name="shouldCreateRma"
          render={({ field: { value, onChange } }) => (
            <SwitchInput
              id="xb-fulfillment-create-rma"
              label="Create RMA"
              defaultChecked={value}
              onChange={onChange}
            />
          )}
        />

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function XbFulfillment() {
  const { data } = useIntegrationSettingsData();
  const { mutate: saveChanges } = useIntegrationSettingsUpdate();

  const { xbFulfillmentConfig } = data ?? {};

  const [showForm, setShowForm] = useState(false);

  const xBFulfillmentEnabled = !!xbFulfillmentConfig;

  const disconnectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;

    if (!checked && xbFulfillmentConfig) {
      saveChanges({
        kind: 'xbFulfillmentConfig',
        xbFulfillmentConfig: { action: 'disconnect' },
      });
    } else {
      setShowForm(true);
    }
  };

  return (
    <>
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/xb-fulfillment.png"
        title="Xb-Fulfillment"
        worksWith={returnWarranty}
        description={
          xbFulfillmentConfig
            ? 'XB Fulfillment is connected.'
            : 'Connect an XB Fulfillment account to create RMA requests.'
        }
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="xb-fulfillment-switch"
              label="Connect or disconnect XB Fulfillment account"
              labelVisuallyHidden
              checked={xBFulfillmentEnabled}
              onChange={disconnectHandler}
            />
            {xBFulfillmentEnabled && (
              <Action
                icon="settings"
                onClick={() => setShowForm(true)}
                accessibilityLabel="Edit XB Fulfillment Configuration"
              />
            )}
          </div>
        }
      />
      <XbFulfillmentForm
        showForm={showForm}
        values={xbFulfillmentConfig}
        closeForm={() => setShowForm(false)}
      />
    </>
  );
}

function PostscriptConfig() {
  return (
    <IntegrationCard
      imgUrl="https://cdn.corso.com/img/postscript.png"
      title="Postscript"
      worksWith={returnWarranty}
      description="Coming soon!"
      action={
        <SwitchInput
          id="postscript-switch"
          label="Connect or disconnect a Postscript account"
          labelVisuallyHidden
          checked={false}
          disabled
        />
      }
    />
  );
}

const getConnectedText = (requiresUpdate: boolean, appUrl: string) => {
  if (requiresUpdate) {
    return (
      <Alert heading="Gorgias App Update Required" variant="warning">
        <Action variant="link" to={appUrl}>
          View app
        </Action>
      </Alert>
    );
  }
  return 'Gorgias is connected.';
};

function GorgiasConfig() {
  const { data: { gorgiasConfig } = {} } = useIntegrationSettingsData();

  const gorgiasEnabled = gorgiasConfig?.connected;

  const appRequiresUpdate =
    gorgiasConfig && gorgiasConfig.connected && gorgiasConfig.updateRequired;

  const description = gorgiasConfig?.connected ? (
    getConnectedText(!!appRequiresUpdate, gorgiasConfig.appUrl)
  ) : (
    <div className="space-y-1">
      <p>
        {' '}
        Connect a Gorgias account.{' '}
        <Action
          variant="link"
          to="https://help.corso.com/gorgias-app-integration"
          target="_blank"
          rel="noreferrer"
        >
          Learn More
        </Action>
      </p>
    </div>
  );

  const changeHandler = () =>
    gorgiasEnabled
      ? window.open(gorgiasConfig.appUrl, '_blank')
      : window.open(
          'https://www.gorgias.com/apps/corso-post-purchase',
          '_blank',
        );

  return (
    <IntegrationCard
      worksWith={returnWarrantyProtect}
      imgUrl="https://cdn.corso.com/img/gorgias-2.png"
      title="Gorgias"
      description={description}
      action={
        <SwitchInput
          id="gorgias-switch"
          label="Connect or disconnect Gorgias account"
          labelVisuallyHidden
          defaultChecked={gorgiasEnabled}
          onChange={changeHandler}
        />
      }
    />
  );
}

function GladlyConfig() {
  const { data } = useIntegrationSettingsData();
  const { gladlyConfig } = data ?? {};
  return (
    <IntegrationCard
      worksWith={returnWarrantyProtect}
      imgUrl="https://cdn.corso.com/img/gladly-1.png"
      title="Gladly"
      description={
        gladlyConfig
          ? 'Please contact us to manage your Gladly account.'
          : 'Please contact us to connect your Gladly account.'
      }
      action={
        <SwitchInput
          id="gladly-switch"
          label="Connect or disconnect a Gladly account"
          labelVisuallyHidden
          defaultChecked={!!gladlyConfig}
          disabled
        />
      }
    />
  );
}

function LocalizeConfig() {
  const { data } = useIntegrationSettingsData();
  const { localizeConfig } = data ?? {};
  return (
    <IntegrationCard
      worksWith={returnWarrantyProtect}
      imgUrl="https://cdn.corso.com/img/localize2.png"
      title="Localize"
      description={
        localizeConfig
          ? 'Please contact us to manage your Localize account.'
          : 'Please contact us to connect your Localize account.'
      }
      action={
        <SwitchInput
          id="gladly-switch"
          label="Connect or disconnect a Localize account"
          labelVisuallyHidden
          defaultChecked={!!localizeConfig}
          disabled
        />
      }
    />
  );
}

function KustomerForm({
  showForm,
  closeForm,
  values,
}: {
  showForm: boolean;
  closeForm: () => void;
  values: IntegrationSettings['kustomerConfig'] | undefined;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();

  const toast = useToast();

  const {
    handleSubmit,

    formState: { errors, isDirty, isSubmitting },
    register,
    reset,
  } = useForm<CrewMerchantUi.KustomerConfigForm>({
    resolver: zodResolver(CrewMerchantUi.kustomerConfigFormSchema),
    values: {
      apiKey: '',
      isEnabled: values?.isEnabled ?? true,
      triggerResync: false,
    },
  });

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const submitHandler: FormEventHandler<HTMLFormElement> = (event) => {
    handleSubmit(({ apiKey, ...config }) => {
      const connectedUpdate = apiKey
        ? ({ action: 'rotateSecret', apiKey, ...config } as const)
        : ({ action: 'configure', ...config } as const);

      return saveChanges({
        kind: 'kustomerConfig',
        kustomerConfig: values
          ? // connected: a value rotates the secret, blank keeps the stored one
            connectedUpdate
          : { action: 'connect', apiKey, ...config },
      })
        .then(closeHandler)
        .catch(console.error);
    }, console.error)(event).catch(console.error);
  };

  const appSettings = values?.appSettings ?? [];

  const permissions = [
    'org.admin.apps',
    'org.permission.customer.read',
    'org.permission.kobject',
    'org.permission.klass',
  ];

  const resyncHandler = () => {
    if (values) {
      toast.show('Resyncing Kustomer data...This may take a few minutes.');
      saveChanges({
        kind: 'kustomerConfig',
        kustomerConfig: {
          action: 'configure',
          isEnabled: values.isEnabled,
          triggerResync: true,
        },
      }).catch(console.error);
    }
  };

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="Kustomer"
      description="Configure your Kustomer account."
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Alert heading="Multi-Store Accounts" variant="tip">
          If you are using Kustomer across multiple stores, please use the same
          API key for all stores.
        </Alert>

        <PasswordInput
          id="kustomer-api-key"
          label="Kustomer API Key"
          {...secretInputProps(
            values?.maskedApiKey,
            'The API Key used to connect to Kustomer, please ensure it has the necessary permissions, and is set not expire.',
          )}
          {...register('apiKey')}
          error={errors.apiKey?.message}
        />

        <div className="flex flex-col gap-2">
          <Label>Kustomer API Permissions</Label>

          {permissions.map((permission) => (
            <div className="flex items-center gap-2" key={permission}>
              <ClipboardButton onClick={() => permission} variant="ghost" />
              <Badge>{permission}</Badge>
            </div>
          ))}
        </div>

        {appSettings.length > 0 && (
          <div className="flex flex-col gap-2">
            <Label>Corso App Settings</Label>
            <Alert variant="info" heading="Add Your Kustomer API Key">
              Once added, Corso can be installed and configured using the
              following settings.
            </Alert>
            {appSettings.map((setting) => (
              <div className="flex items-center gap-2" key={setting.key}>
                <ClipboardButton
                  onClick={() => setting.value}
                  variant="ghost"
                />
                <Badge>{setting.label}</Badge>
              </div>
            ))}
          </div>
        )}

        {values && (
          <>
            <div>
              <Label>Corso Data Connection</Label>
              <p className="text-xs text-neutral-500">
                {`  After installing the app in Kustomer, you’ll be able to sync
                historical data from Corso. This process will only need to be
                done once`}
              </p>
            </div>
            <Action
              icon={faSync}
              onClick={() => resyncHandler()}
              accessibilityLabel="Resync Data"
            >
              Sync
            </Action>
          </>
        )}

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function KustomerConfig() {
  const { data: { kustomerConfig } = {} } = useIntegrationSettingsData();
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const [showForm, setShowForm] = useState(false);

  const connectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;
    if (kustomerConfig) {
      saveChanges({
        kind: 'kustomerConfig',
        kustomerConfig: { action: 'configure', isEnabled: checked },
      }).catch(console.error);
    } else {
      setShowForm(true);
    }
  };

  const description =
    // oxlint-disable-next-line no-nested-ternary
    kustomerConfig
      ? kustomerConfig.isEnabled
        ? 'Kustomer is enabled.'
        : 'Kustomer is disabled.'
      : 'Connect a Kustomer account.';

  return (
    <>
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/kustomer-4.png"
        title="Kustomer"
        worksWith={returnWarrantyProtect}
        description={description}
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="kustomer-switch"
              label="Connect a Kustomer account"
              labelVisuallyHidden
              checked={!!kustomerConfig?.isEnabled}
              onChange={connectHandler}
            />
            {kustomerConfig && (
              <Action
                icon="settings"
                onClick={() => setShowForm(true)}
                accessibilityLabel="Edit Kustomer Configuration"
              />
            )}
          </div>
        }
      />

      <KustomerForm
        showForm={showForm}
        closeForm={() => setShowForm(false)}
        values={kustomerConfig}
      />
    </>
  );
}

function ZowieConfig() {
  const { data } = useIntegrationSettingsData();
  const { zowieConfig } = data ?? {};
  return (
    <IntegrationCard
      worksWith={builtForShippingProtection}
      imgUrl="https://cdn.corso.com/img/zowie.png"
      title="Zowie"
      description={
        zowieConfig
          ? 'Please contact us to manage your Zowie account.'
          : 'Please contact us to connect your Zowie account.'
      }
      action={
        <SwitchInput
          id="zowie-switch"
          label="Connect or disconnect a Zowie account"
          labelVisuallyHidden
          defaultChecked={!!zowieConfig}
          disabled
        />
      }
    />
  );
}

function GlobalEForm({
  showForm,
  closeForm,
  values,
}: {
  showForm: boolean;
  closeForm: () => void;
  values: IntegrationSettings['globalEConfig'] | undefined;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();

  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    register,
    reset,
  } = useForm({
    resolver: zodResolver(CrewMerchantUi.globalEUpdateSchema),
    values: {
      kind: 'globalEConfig',
      globalEConfig: values ?? {
        merchantGuid: '',
        replacementOrderShippingTitle: undefined,
        isEnabled: true,
      },
    },
  });

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const submitHandler: FormEventHandler<HTMLFormElement> = (event) => {
    handleSubmit(
      (updates) => saveChanges(updates).then(closeHandler).catch(console.error),
      console.error,
    )(event).catch(console.error);
  };

  return (
    <Modal show={showForm} onClose={closeForm} title="Global-e Configuration">
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="global-e-merchant-guid"
          label="Merchant GUID"
          {...register('globalEConfig.merchantGuid')}
          error={
            'globalEConfig' in errors
              ? errors.globalEConfig?.merchantGuid?.message
              : undefined
          }
          details="The Merchant GUID from your Global-e account."
          required
        />
        <TextInput
          id="global-e-replacement-order-shipping-title"
          label="Replacement Order Shipping Title"
          {...register('globalEConfig.replacementOrderShippingTitle')}
          error={
            'globalEConfig' in errors
              ? errors.globalEConfig?.replacementOrderShippingTitle?.message
              : undefined
          }
          details="Shipping line title applied to replacement orders created for Global-e claims."
        />

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function GlobalEConfig() {
  const { data } = useIntegrationSettingsData();
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const { globalEConfig } = data ?? {};
  const [showForm, setShowForm] = useState(false);

  const connectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;
    if (globalEConfig) {
      saveChanges({
        kind: 'globalEConfig',
        globalEConfig: { ...globalEConfig, isEnabled: checked },
      }).catch(console.error);
    } else {
      setShowForm(true);
    }
  };

  return (
    <>
      <IntegrationCard
        worksWith={returnWarranty}
        imgUrl="https://cdn.corso.com/img/globale-2.png"
        title="Global-e"
        description={
          // oxlint-disable-next-line no-nested-ternary
          globalEConfig
            ? globalEConfig.isEnabled
              ? 'Global-e is enabled.'
              : 'Global-e is disabled.'
            : 'Connect a Global-e account.'
        }
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="global-e-switch"
              label="Connect or disconnect a Global-e account"
              labelVisuallyHidden
              checked={!!globalEConfig?.isEnabled}
              onChange={connectHandler}
            />
            {globalEConfig && (
              <Action
                icon="settings"
                onClick={() => setShowForm(true)}
                accessibilityLabel="Manage Global-e Account"
              />
            )}
          </div>
        }
      />
      {showForm && (
        <GlobalEForm
          showForm={showForm}
          values={globalEConfig}
          closeForm={() => setShowForm(false)}
        />
      )}
    </>
  );
}

function ZendeskConfig() {
  const { mutate: saveChanges, isPending } = useIntegrationSettingsUpdate();
  const zendeskParams = useZendeskParams();
  const isCurrentInstall = useIsCurrentZendesk();
  const { data } = useIntegrationSettingsData();
  const { zendeskConfig } = data ?? {};

  return (
    <IntegrationCard
      id="zendesk"
      worksWith={returnWarrantyProtect}
      imgUrl="https://cdn.corso.com/img/zendesk.png"
      title="Zendesk"
      description={
        zendeskConfig
          ? 'Please contact us to manage your Zendesk account.'
          : 'Connect a Zendesk account.'
      }
      action={
        !isCurrentInstall && zendeskParams ? (
          <Action
            variant="primary"
            disabled={isPending}
            onClick={() => {
              saveChanges({
                kind: 'zendeskConfig',
                zendeskConfig: zendeskParams,
              });
            }}
          >
            Finish Connecting
          </Action>
        ) : (
          <SwitchInput
            id="zendesk-switch"
            label="Connect a Zendesk account"
            labelVisuallyHidden
            defaultChecked={!!zendeskConfig}
            disabled={!!zendeskConfig}
            onChange={(event) => {
              // TODO evaluate URL after update to see if it needs changed
              if (event.target.checked) {
                window.open(
                  'https://www.zendesk.com/marketplace/apps/support/897775',
                  '_top',
                );
                return;
              }

              throw new Error('Disconnecting Zendesk Not Implemented'); // shouldn't be reachable as this should be disabled when connected
            }}
          />
        )
      }
    />
  );
}

function LogiwaConfig() {
  const { data } = useIntegrationSettingsData();
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const { logiwaConfig } = data ?? {};

  const connectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;
    if (logiwaConfig) {
      saveChanges({
        kind: 'logiwaConfig',
        logiwaConfig: { ...logiwaConfig, shouldCreateRma: checked },
      }).catch(console.error);
    }
  };

  return (
    <IntegrationCard
      worksWith={returnWarranty}
      imgUrl="https://cdn.corso.com/img/logiwa-2.png"
      title="Logiwa"
      description={
        // oxlint-disable-next-line no-nested-ternary
        logiwaConfig
          ? logiwaConfig.shouldCreateRma
            ? 'Logiwa is enabled to create RMA requests.'
            : 'Logiwa is disabled, RMA requests will not be created.'
          : 'Please contact us to connect your Logiwa account.'
      }
      action={
        <SwitchInput
          id="logiwa-switch"
          label="Connect or disconnect a Logiwa account"
          labelVisuallyHidden
          checked={!!logiwaConfig?.shouldCreateRma}
          onChange={connectHandler}
          disabled={!logiwaConfig}
        />
      }
    />
  );
}

function HappyReturnsConfig() {
  const { data } = useIntegrationSettingsData();
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const { happyReturnsConfig } = data ?? {};

  const connectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;
    if (!happyReturnsConfig) return;

    saveChanges({
      kind: 'happyReturnsConfig',
      happyReturnsConfig: { isEnabled: checked },
    }).catch(console.error);
  };

  return (
    <IntegrationCard
      worksWith={returnWarranty}
      imgUrl="https://cdn.corso.com/img/happy-returns-logo.png"
      title="Happy Returns"
      description={
        // oxlint-disable-next-line no-nested-ternary
        happyReturnsConfig
          ? happyReturnsConfig.isEnabled
            ? 'Happy Returns is enabled.'
            : 'Happy Returns is disabled.'
          : 'Please contact us to connect your Happy Returns account.'
      }
      action={
        <SwitchInput
          id="happy-returns-switch"
          label="Enable or disable Happy Returns"
          labelVisuallyHidden
          checked={!!happyReturnsConfig?.isEnabled}
          onChange={connectHandler}
          disabled={!happyReturnsConfig}
        />
      }
    />
  );
}

function TwoBoxesForm({
  showForm,
  closeForm,
  values,
}: {
  showForm: boolean;
  closeForm: () => void;
  values: IntegrationSettings['twoBoxesConfig'] | undefined;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();

  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    register,
    reset,
  } = useForm<CrewMerchantUi.TwoBoxesConfigForm>({
    resolver: zodResolver(CrewMerchantUi.twoBoxesConfigFormSchema),
    values: {
      hmacSignatureSecret: '',
      webhookToken: '',
      webhookId: values?.webhookId ?? '',
      isEnabled: values?.isEnabled ?? true,
    },
  });

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const submitHandler: FormEventHandler<HTMLFormElement> = (event) => {
    handleSubmit(({ webhookToken, hmacSignatureSecret, ...config }) => {
      // Two Boxes has two secrets and either can be rotated on its own
      const isRotating = Boolean(webhookToken) || Boolean(hmacSignatureSecret);

      const connectedUpdate = isRotating
        ? ({
            action: 'rotateSecret',
            ...config,
            ...(webhookToken ? { webhookToken } : {}),
            ...(hmacSignatureSecret ? { hmacSignatureSecret } : {}),
          } as const)
        : ({ action: 'configure', ...config } as const);

      return saveChanges({
        kind: 'twoBoxesConfig',
        twoBoxesConfig: values
          ? // connected: a value rotates that secret, blank keeps the stored one
            connectedUpdate
          : {
              action: 'connect',
              ...config,
              webhookToken,
              hmacSignatureSecret,
            },
      })
        .then(closeHandler)
        .catch(console.error);
    }, console.error)(event).catch(console.error);
  };

  return (
    <Modal
      show={showForm}
      onClose={closeForm}
      title="Two Boxes Configuration"
      size="lg"
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        {values?.corsoWebhookUrl && (
          <UrlInput
            id="two-boxes-url"
            label="Corso Webhook URL"
            required
            details="Provide this URL to your Two Boxes representative to configure webhooks."
            value={values.corsoWebhookUrl}
            readOnly
            addon={{
              outsideEnd: (
                // using `grid` on mobile and `flex` on desktop avoid apply `grow` to each child
                <div className="grid grid-cols-2 items-center gap-2 md:flex">
                  {/* // ? adjust to be styled as a button group  */}
                  <ClipboardButton onClick={() => values.corsoWebhookUrl} />
                </div>
              ),
            }}
          />
        )}
        <PasswordInput
          id="two-boxes-webhook-token"
          label="Webhook Token"
          {...secretInputProps(
            values?.maskedWebhookToken,
            'This token is used to authenticate both inbound and outbound webhooks with Two Boxes.',
          )}
          {...register('webhookToken')}
          error={errors.webhookToken?.message}
        />
        <PasswordInput
          id="two-boxes-hmac-signature-secret"
          label="Webhook HMAC Secret"
          {...secretInputProps(
            values?.maskedHmacSignatureSecret,
            'This secret is used to validate incoming webhooks from Two Boxes.',
          )}
          {...register('hmacSignatureSecret')}
          error={errors.hmacSignatureSecret?.message}
        />
        <TextInput
          id="two-boxes-webhook-id"
          label="Webhook ID"
          {...register('webhookId')}
          addon={{
            insideStart: (
              <span className="text-neutral-500">
                https://api.twoboxes.io/webhooks/
              </span>
            ),
          }}
          details="This is the ID of the webhook created in Two Boxes to send events to Corso."
          error={errors.webhookId?.message}
          required
        />

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function TwoBoxesConfig() {
  const { data } = useIntegrationSettingsData();
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const { twoBoxesConfig } = data ?? {};
  const [showForm, setShowForm] = useState(false);

  const connectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;
    if (twoBoxesConfig) {
      saveChanges({
        kind: 'twoBoxesConfig',
        twoBoxesConfig: {
          action: 'configure',
          isEnabled: checked,
          webhookId: twoBoxesConfig.webhookId,
        },
      }).catch(console.error);
    } else {
      setShowForm(true);
    }
  };

  return (
    <>
      <IntegrationCard
        worksWith={returnWarranty}
        imgUrl="https://cdn.corso.com/img/two-boxes-2.png"
        title="Two Boxes"
        description={
          // oxlint-disable-next-line no-nested-ternary
          twoBoxesConfig
            ? twoBoxesConfig.isEnabled
              ? 'TwoBoxes is enabled to create RMA requests.'
              : 'TwoBoxes is disabled, RMA requests will not be created.'
            : 'Connect a Two Boxes account to create RMA requests.'
        }
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="two-boxes-switch"
              label="Connect or disconnect a Two Boxes account"
              labelVisuallyHidden
              checked={!!twoBoxesConfig?.isEnabled}
              onChange={connectHandler}
            />
            {twoBoxesConfig && (
              <Action
                icon="settings"
                onClick={() => setShowForm(true)}
                accessibilityLabel="Manage Two Boxes Account"
              />
            )}
          </div>
        }
      />
      {showForm && (
        <TwoBoxesForm
          showForm={showForm}
          values={twoBoxesConfig}
          closeForm={() => setShowForm(false)}
        />
      )}
    </>
  );
}

function BrijConfigForm({
  showForm,
  closeForm,
  customerAppUrl,
}: {
  customerAppUrl: string;
  showForm: boolean;
  closeForm: () => void;
}) {
  const closeHandler = () => {
    closeForm();
  };

  return (
    <Modal show={showForm} onClose={closeHandler} title="Brij Configuration">
      <form className="flex flex-col gap-4">
        <UrlInput
          id="customer-portal"
          label="Start a Warranty Request"
          details="Provide this URL to your Brij representative to direct customers to start a warranty request."
          value={customerAppUrl}
          required
          readOnly
          disabled
          addon={{
            outsideEnd: (
              <div className="grid grid-cols-2 items-center gap-2 md:flex">
                <ClipboardButton onClick={() => customerAppUrl} />
                <Action
                  to={customerAppUrl}
                  target="_blank"
                  accessibilityLabel="Visit"
                  icon="externalLink"
                />
              </div>
            ),
          }}
        />
        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action variant="primary" type="submit">
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function BrijConfig() {
  const [showForm, setShowForm] = useState(false);
  const { data: { brijConfig } = {} } = useIntegrationSettingsData();

  const brijEnabled = !!brijConfig;

  return (
    <>
      <IntegrationCard
        worksWith={returnWarranty}
        imgUrl="https://cdn.corso.com/img/brij.png"
        title="Brij"
        description={
          brijConfig
            ? 'Brij is connected.'
            : 'Please contact us to connect your Brij account.'
        }
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="brij-switch"
              label="Connect or disconnect a Brij account"
              labelVisuallyHidden
              defaultChecked={brijEnabled}
            />

            {brijEnabled && (
              <Action
                icon="settings"
                onClick={() => {
                  setShowForm(true);
                }}
                accessibilityLabel="Edit Brij Configuration"
              />
            )}
          </div>
        }
      />
      {brijEnabled && (
        <BrijConfigForm
          customerAppUrl={brijConfig.startRegistrationUrl}
          showForm={showForm}
          closeForm={() => setShowForm(false)}
        />
      )}
    </>
  );
}

function CcsAlert() {
  return (
    <Alert variant="info" heading="Carrier Calculated Service In Use">
      Disabling this service may prevent shipping rates from being calculated at
      checkout.
    </Alert>
  );
}

type Carrier = NonNullable<
  IntegrationSettings['easyPostConfig']
>['enabledCarriers'][number];
function getCarrierOptions(carriers: Carrier[]) {
  return carriers.map((carrier) => ({
    label: (
      <div>
        {carrier.name}
        <p className="text-xs text-neutral-500">{carrier.idFromPlatform}</p>
      </div>
    ),
    value: carrier.id.toString(),
    searchValue: carrier.name,
  })) satisfies MultiSelectOption[];
}

function VesylConfigForm({
  showForm,
  config,
  closeForm,
}: {
  showForm: boolean;
  config: IntegrationSettings['vesylConfig'] | undefined;
  closeForm: () => void;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const { mutate: refreshCarriers, isPending: isRefreshing } =
    useShippingProviderRefresh();
  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    register,
    reset,
    control,
  } = useForm<CrewMerchantUi.ShippingProviderForm>({
    resolver: zodResolver(CrewMerchantUi.shippingProviderFormSchema),
    values: {
      apiKey: '',
      provider: 'Vesyl',
      isEnabled: config?.isEnabled ?? true,
      isCarrierCalculatedServiceEnabled:
        config?.isCarrierCalculatedServiceEnabled ?? false,
      enabledCarriers: config?.enabledCarriers ?? [],
      enabledCarriersMerchant: config?.enabledCarriersMerchant ?? [],
    },
  });

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ apiKey, ...providerConfig }) => {
      const connectedUpdate = apiKey
        ? ({ action: 'rotateSecret', apiKey, ...providerConfig } as const)
        : ({ action: 'configure', ...providerConfig } as const);

      return saveChanges({
        kind: 'vesylConfig',
        vesylConfig: config
          ? // connected: a value rotates the secret, blank keeps the stored one
            connectedUpdate
          : { action: 'connect', apiKey, ...providerConfig },
      })
        .then(() => closeHandler())
        .catch(console.error);
    }, console.error)(event).catch(console.error);
  };

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="Vesyl Configuration"
      description="Configure Vesyl to generate shipping labels."
    >
      <form className="flex flex-col gap-4" onSubmit={submitHandler}>
        <PasswordInput
          id="shipping-provider-api-key"
          label="API Key"
          {...secretInputProps(config?.maskedApiKey)}
          {...register('apiKey')}
          error={errors.apiKey?.message}
        />

        <Controller
          control={control}
          name="isCarrierCalculatedServiceEnabled"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="carrier-calculated-service"
              label="Carrier Calculated Service"
              details="When enabled, this service will be used to calculate shipping rates at checkout."
              checked={value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        {config && (
          <Action
            icon="refresh"
            variant="ghost"
            accessibilityLabel="Refresh Vesyl carrier accounts"
            loading={isRefreshing}
            onClick={() => refreshCarriers({ provider: 'Vesyl' })}
          >
            Refresh Carriers
          </Action>
        )}

        {!!config?.carriers.length && (
          <>
            <Controller
              name="enabledCarriers"
              control={control}
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  label="Customer Portal Allowed Carriers"
                  placeholder="Search for a carrier"
                  options={getCarrierOptions(config.carriers)}
                  value={value.map((carrier) => ({
                    label: carrier.name,
                    value: carrier.id.toString(),
                    searchValue: carrier.name,
                  }))}
                  onChange={(selected) => {
                    onChange(
                      selected.map((selectedCarrier) =>
                        config.carriers.find(
                          ({ id }) => id.toString() === selectedCarrier.value,
                        ),
                      ),
                    );
                  }}
                  details="Carriers to select from when generating shipping labels in the Customer Portal."
                  error={errors.enabledCarriers?.message}
                />
              )}
            />

            <Controller
              name="enabledCarriersMerchant"
              control={control}
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  label="Merchant Admin Allowed Carriers"
                  placeholder="Search for a carrier"
                  options={getCarrierOptions(config.carriers)}
                  value={value.map((carrier) => ({
                    label: carrier.name,
                    value: carrier.id.toString(),
                    searchValue: carrier.name,
                  }))}
                  onChange={(selected) => {
                    onChange(
                      selected.map((selectedCarrier) =>
                        config.carriers.find(
                          ({ id }) => id.toString() === selectedCarrier.value,
                        ),
                      ),
                    );
                  }}
                  details="Carriers to select from when generating shipping labels in the Merchant Admin."
                  error={errors.enabledCarriersMerchant?.message}
                />
              )}
            />
          </>
        )}

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function VesylConfig() {
  const { data: { vesylConfig } = {} } = useIntegrationSettingsData();
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const [showForm, setShowForm] = useState(false);

  const description = vesylConfig ? (
    `Vesyl is ${vesylConfig.isEnabled ? 'enabled.' : 'disabled.'}`
  ) : (
    <div className="space-y-1">
      <p>Connect a Vesyl account to generate shipping labels.</p>
      <p>
        <Action
          variant="link"
          to="https://app.vesyl.com/register?ref=corso"
          target="_blank"
          rel="noreferrer"
        >
          Create a Vesyl account.
        </Action>
      </p>
    </div>
  );

  const connectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;
    if (!vesylConfig) {
      setShowForm(true);
    } else {
      saveChanges({
        kind: 'vesylConfig',
        vesylConfig: {
          action: 'configure',
          provider: 'Vesyl',
          isEnabled: checked,
          isCarrierCalculatedServiceEnabled:
            vesylConfig.isCarrierCalculatedServiceEnabled,
          enabledCarriers: vesylConfig.enabledCarriers,
          enabledCarriersMerchant: vesylConfig.enabledCarriersMerchant,
        },
      }).catch(console.error);
    }
  };

  return (
    <>
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/vesyl.png"
        title="Vesyl"
        worksWith={returnWarrantyProtect}
        description={description}
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="connect-vesyl"
              label="Connect a Vesyl account"
              labelVisuallyHidden
              name="vesyl"
              checked={!!vesylConfig?.isEnabled}
              onChange={connectHandler}
            />

            {vesylConfig && (
              <Action
                icon="settings"
                onClick={() => {
                  setShowForm(true);
                }}
                accessibilityLabel="Edit Vesyl Configuration"
              />
            )}
          </div>
        }
      />

      <VesylConfigForm
        showForm={showForm}
        config={vesylConfig}
        closeForm={() => setShowForm(false)}
      />
    </>
  );
}

function EasyPostConfigForm({
  showForm,
  config,
  closeForm,
}: {
  showForm: boolean;
  config: IntegrationSettings['easyPostConfig'] | undefined;
  closeForm: () => void;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const { mutate: refreshCarriers, isPending: isRefreshing } =
    useShippingProviderRefresh();
  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    register,
    reset,
    watch,
    control,
  } = useForm<CrewMerchantUi.ShippingProviderForm>({
    resolver: zodResolver(CrewMerchantUi.shippingProviderFormSchema),
    values: {
      apiKey: '',
      provider: 'Easy_Post',
      isEnabled: config?.isEnabled ?? true,
      isCarrierCalculatedServiceEnabled:
        config?.isCarrierCalculatedServiceEnabled ?? false,
      enabledCarriers: config?.enabledCarriers ?? [],
      enabledCarriersMerchant: config?.enabledCarriersMerchant ?? [],
    },
  });

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ apiKey, ...providerConfig }) => {
      const connectedUpdate = apiKey
        ? ({ action: 'rotateSecret', apiKey, ...providerConfig } as const)
        : ({ action: 'configure', ...providerConfig } as const);

      return saveChanges({
        kind: 'easyPostConfig',
        easyPostConfig: config
          ? // connected: a value rotates the secret, blank keeps the stored one
            connectedUpdate
          : { action: 'connect', apiKey, ...providerConfig },
      })
        .then(() => closeHandler())
        .catch(console.error);
    }, console.error)(event).catch(console.error);
  };

  const isCcsEnabled = watch('isCarrierCalculatedServiceEnabled');

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="EasyPost Configuration"
      description="Configure EasyPost to generate shipping labels."
    >
      <form className="flex flex-col gap-4" onSubmit={submitHandler}>
        <PasswordInput
          id="shipping-provider-api-key"
          label="API Key"
          {...secretInputProps(config?.maskedApiKey)}
          {...register('apiKey')}
          error={errors.apiKey?.message}
        />

        {isCcsEnabled && <CcsAlert />}

        <Controller
          control={control}
          name="isCarrierCalculatedServiceEnabled"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="carrier-calculated-service"
              label="Carrier Calculated Service"
              details="When enabled, this service will be used to calculate shipping rates at checkout."
              checked={value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        {!!config?.carriers.length && (
          <>
            <Controller
              name="enabledCarriers"
              control={control}
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  label="Customer Portal Allowed Carriers"
                  placeholder="Search for a carrier"
                  options={getCarrierOptions(config.carriers)}
                  value={value.map((carrier) => ({
                    label: carrier.name,
                    value: carrier.id.toString(),
                    searchValue: carrier.name,
                  }))}
                  onChange={(selected) => {
                    onChange(
                      selected.map((selectedCarrier) =>
                        config.carriers.find(
                          ({ id }) => id.toString() === selectedCarrier.value,
                        ),
                      ),
                    );
                  }}
                  details="Carriers to select from when generating shipping labels in the Customer Portal."
                  error={errors.enabledCarriers?.message}
                />
              )}
            />

            <Controller
              name="enabledCarriersMerchant"
              control={control}
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  label="Merchant Admin Allowed Carriers"
                  placeholder="Search for a carrier"
                  options={getCarrierOptions(config.carriers)}
                  value={value.map((carrier) => ({
                    label: carrier.name,
                    value: carrier.id.toString(),
                    searchValue: carrier.name,
                  }))}
                  onChange={(selected) => {
                    onChange(
                      selected.map((selectedCarrier) =>
                        config.carriers.find(
                          ({ id }) => id.toString() === selectedCarrier.value,
                        ),
                      ),
                    );
                  }}
                  details="Carriers to select from when generating shipping labels in the Merchant Admin."
                  error={errors.enabledCarriersMerchant?.message}
                />
              )}
            />
          </>
        )}

        {config && (
          <Action
            icon="refresh"
            variant="ghost"
            accessibilityLabel="Refresh EasyPost carrier accounts"
            loading={isRefreshing}
            onClick={() => refreshCarriers({ provider: 'Easy_Post' })}
          >
            Refresh Carriers
          </Action>
        )}

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function EasyPostConfig() {
  const { data: { easyPostConfig } = {} } = useIntegrationSettingsData();
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const [showForm, setShowForm] = useState(false);
  const description = easyPostConfig
    ? `EasyPost is ${easyPostConfig.isEnabled ? 'enabled.' : 'disabled.'}`
    : 'Connect an EasyPost account to generate shipping labels.';

  const connectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;
    if (!easyPostConfig) {
      setShowForm(true);
    } else {
      saveChanges({
        kind: 'easyPostConfig',
        easyPostConfig: {
          action: 'configure',
          provider: 'Easy_Post',
          isEnabled: checked,
          isCarrierCalculatedServiceEnabled:
            easyPostConfig.isCarrierCalculatedServiceEnabled,
          enabledCarriers: easyPostConfig.enabledCarriers,
          enabledCarriersMerchant: easyPostConfig.enabledCarriersMerchant,
        },
      }).catch(console.error);
    }
  };

  return (
    <>
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/easypost-1.png"
        title="EasyPost"
        description={description}
        worksWith={returnWarrantyProtect}
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="connect-easyPost"
              label="Connect a EasyPost account"
              labelVisuallyHidden
              name="EasyPost"
              checked={!!easyPostConfig?.isEnabled}
              onChange={connectHandler}
            />

            {easyPostConfig && (
              <Action
                icon="settings"
                onClick={() => {
                  setShowForm(true);
                }}
                accessibilityLabel="Edit EasyPost Configuration"
              />
            )}
          </div>
        }
      />

      <EasyPostConfigForm
        showForm={showForm}
        config={easyPostConfig}
        closeForm={() => setShowForm(false)}
      />
    </>
  );
}

function RechargeConfigForm({
  showForm,
  config,
  closeForm,
}: {
  showForm: boolean;
  config: IntegrationSettings['rechargeConfig'] | undefined;
  closeForm: () => void;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    register,
    reset,
  } = useForm<CrewMerchantUi.RechargeConfigForm>({
    resolver: zodResolver(CrewMerchantUi.rechargeConfigFormSchema),
    values: {
      apiKey: '',
      isEnabled: config?.isEnabled ?? true,
    },
  });

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ apiKey, ...rechargeConfig }) => {
      const connectedUpdate = apiKey
        ? ({ action: 'rotateSecret', apiKey, ...rechargeConfig } as const)
        : ({ action: 'configure', ...rechargeConfig } as const);

      return saveChanges({
        kind: 'rechargeConfig',
        rechargeConfig: config
          ? // connected: a value rotates the secret, blank keeps the stored one
            connectedUpdate
          : { action: 'connect', apiKey, ...rechargeConfig },
      })
        .then(() => closeHandler())
        .catch(console.error);
    }, console.error)(event).catch(console.error);
  };

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="Recharge Configuration"
      description="Configure Recharge to offer Shipping Protection to subscription customers."
    >
      <form className="flex flex-col gap-4" onSubmit={submitHandler}>
        <PasswordInput
          id="recharge-api-key"
          label="API Key"
          {...secretInputProps(config?.maskedApiKey)}
          {...register('apiKey')}
          error={errors.apiKey?.message}
        />

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function RechargeConfig() {
  const { data: { rechargeConfig } = {} } = useIntegrationSettingsData();
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const [showForm, setShowForm] = useState(false);
  const description = rechargeConfig
    ? `Recharge is ${rechargeConfig.isEnabled ? 'enabled.' : 'disabled.'}`
    : 'Connect a Recharge account.';

  const connectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;
    if (!rechargeConfig) {
      setShowForm(true);
    } else {
      saveChanges({
        kind: 'rechargeConfig',
        rechargeConfig: { action: 'configure', isEnabled: checked },
      }).catch(console.error);
    }
  };

  return (
    <>
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/recharge-1.png"
        title="Recharge"
        description={description}
        worksWith={builtForShippingProtection}
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="connect-recharge"
              label="Connect a Recharge account."
              labelVisuallyHidden
              name="Recharge"
              checked={!!rechargeConfig?.isEnabled}
              onChange={connectHandler}
            />

            {rechargeConfig && (
              <Action
                icon="settings"
                onClick={() => {
                  setShowForm(true);
                }}
                accessibilityLabel="Edit Recharge Configuration"
              />
            )}
          </div>
        }
      />

      <RechargeConfigForm
        showForm={showForm}
        config={rechargeConfig}
        closeForm={() => setShowForm(false)}
      />
    </>
  );
}

function SkioConfigForm({
  showForm,
  config,
  closeForm,
}: {
  showForm: boolean;
  config: IntegrationSettings['skioConfig'] | undefined;
  closeForm: () => void;
}) {
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();
  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    register,
    reset,
  } = useForm<CrewMerchantUi.SkioConfigForm>({
    resolver: zodResolver(CrewMerchantUi.skioConfigFormSchema),
    values: {
      accessToken: '',
      isEnabled: config?.isEnabled ?? true,
      webhookToken: config?.webhookToken ?? '',
    },
  });

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const { webhookUrl } = config ?? {};

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ accessToken, ...skioConfig }) => {
      const connectedUpdate = accessToken
        ? ({ action: 'rotateSecret', accessToken, ...skioConfig } as const)
        : ({ action: 'configure', ...skioConfig } as const);

      return saveChanges({
        kind: 'skioConfig',
        skioConfig: config
          ? // connected: a value rotates the secret, blank keeps the stored one
            connectedUpdate
          : { action: 'connect', accessToken, ...skioConfig },
      })
        .then(closeHandler)
        .catch(console.error);
    }, console.error)(event).catch(console.error);
  };

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="Skio Configuration"
      description="Configure Skio to offer Shipping Protection to subscription customers."
    >
      <form className="flex flex-col gap-4" onSubmit={submitHandler}>
        <PasswordInput
          id="skio-api-key"
          label="API Key"
          {...secretInputProps(config?.maskedAccessToken)}
          {...register('accessToken')}
          error={errors.accessToken?.message}
        />

        {webhookUrl && (
          <>
            <UrlInput
              id="webhook-url"
              label="Webhook URL"
              details="Add this URL to your Skio admin."
              value={webhookUrl}
              required
              readOnly
              disabled
              addon={{
                outsideEnd: (
                  // using `grid` on mobile and `flex` on desktop avoid apply `grow` to each child
                  <div className="grid grid-cols-2 items-center gap-2 md:flex">
                    {/* // ? adjust to be styled as a button group  */}
                    <ClipboardButton onClick={() => webhookUrl} />
                  </div>
                ),
              }}
            />
            <TextInput
              id="webhook-token"
              label="Webhook Token"
              details="The token is provided, after inserting the above webhook URL in the Skio Admin."
              {...register('webhookToken')}
              error={errors.webhookToken?.message}
            />
          </>
        )}

        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeHandler}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function SkioConfig() {
  const { data: { skioConfig } = {} } = useIntegrationSettingsData();
  const { mutateAsync: saveChanges } = useIntegrationSettingsUpdate();

  const [showForm, setShowForm] = useState(false);
  const description = skioConfig
    ? `Skio is ${skioConfig.isEnabled ? 'enabled.' : 'disabled.'}`
    : 'Connect a Skio account.';

  const connectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;
    if (!skioConfig) {
      setShowForm(true);
    } else {
      saveChanges({
        kind: 'skioConfig',
        skioConfig: {
          action: 'configure',
          isEnabled: checked,
          webhookToken: skioConfig.webhookToken,
        },
      }).catch(console.error);
    }
  };

  return (
    <>
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/skio.png"
        title="Skio"
        description={description}
        worksWith={builtForShippingProtection}
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="connect-skio"
              label="Connect a Skio account."
              labelVisuallyHidden
              name="Skio"
              checked={!!skioConfig?.isEnabled}
              onChange={connectHandler}
            />

            {skioConfig && (
              <Action
                icon="settings"
                onClick={() => {
                  setShowForm(true);
                }}
                accessibilityLabel="Edit Skio Configuration"
              />
            )}
          </div>
        }
      />

      <SkioConfigForm
        showForm={showForm}
        config={skioConfig}
        closeForm={() => setShowForm(false)}
      />
    </>
  );
}

function IntelligemsForm({
  showForm,
  config,
  closeForm,
}: {
  showForm: boolean;
  config: ShippingPlusConfig['intelligems'];
  closeForm: () => void;
}) {
  return (
    <Modal
      show={showForm}
      onClose={closeForm}
      title="Intelligems Configuration"
    >
      <div className="mt-2 flex flex-col gap-4">
        <Alert variant="tip" heading="Intelligems Test IDs">
          With the Intelligems integration enabled, Test IDs can be used in
          Shipping Rate Modification rules.
        </Alert>

        <TextInput
          id="intelligems-line-item-attribute-key"
          disabled
          value={config?.lineItemAttributeKey}
          label="Line Item Attribute Key"
          required
        />
        <div className="flex flex-row justify-end gap-2">
          <Action onClick={closeForm}>Close</Action>
        </div>
      </div>
    </Modal>
  );
}

function IntelligemsConfig() {
  const { data } = useConfigSettings(
    ({ shippingProtection }) => shippingProtection,
  );

  const intelligemsConfig = data?.widgetConfig.shippingPlus?.intelligems;

  const [showForm, setShowForm] = useState(false);

  const { mutateAsync: saveChanges } = useConfigSettingsUpdate();

  const description = intelligemsConfig
    ? `Intelligems is ${intelligemsConfig.isEnabled ? 'enabled.' : 'disabled.'}`
    : 'Connect an Intelligems account.';

  if (!data?.widgetConfig.shippingPlus) return null;

  const { widgetConfig } = data;
  const { shippingPlus } = data.widgetConfig;

  const connectHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { checked } = event.target;

    saveChanges({
      shippingProtection: {
        ...data,
        widgetConfig: {
          ...widgetConfig,
          shippingPlus: {
            ...shippingPlus,
            intelligems: {
              ...intelligemsConfig,
              isEnabled: checked,
            },
          },
        },
      },
    }).catch((error) => {
      console.error(error);
    });
  };

  return (
    <>
      <IntegrationCard
        imgUrl="https://cdn.corso.com/img/intelligems.png"
        title="Skio"
        description={description}
        worksWith={builtForShippingProtection}
        action={
          <div className="flex flex-row items-center gap-2">
            <SwitchInput
              id="connect-intelligems"
              label="Connect an Intelligems account."
              labelVisuallyHidden
              name="Intelligems"
              checked={!!intelligemsConfig?.isEnabled}
              onChange={connectHandler}
            />

            {intelligemsConfig && (
              <Action
                icon="settings"
                onClick={() => {
                  setShowForm(true);
                }}
                accessibilityLabel="Edit Intelligems Configuration"
              />
            )}
          </div>
        }
      />

      <IntelligemsForm
        config={intelligemsConfig}
        showForm={showForm}
        closeForm={() => setShowForm(false)}
      />
    </>
  );
}

export default function IntegrationsSettingsPage() {
  return (
    <Page title="Integrations">
      <div className="flex flex-col gap-4">
        <IntegrationSection
          title="E-Commerce Platform"
          id="e-commerce-platform"
        >
          <AppSubscription />
        </IntegrationSection>

        <IntegrationSection title="Shipping Provider" id="shipping-provider">
          <VesylConfig />
          <EasyPostConfig />
          <HappyReturnsConfig />
        </IntegrationSection>

        <IntegrationSection title="Cross-Border" id="cross-border">
          <GlobalEConfig />
        </IntegrationSection>

        <IntegrationSection title="WMS Platform" id="wms-platform">
          <ShipHero />
          <ShipBob />
          <BlueBox />
          <MasonHub />
          <XbFulfillment />
          <LogiwaConfig />
          <TwoBoxesConfig />
        </IntegrationSection>

        <IntegrationSection
          title="Customer Experience"
          id="customer-experience"
        >
          <GorgiasConfig />
          <GladlyConfig />
          <ZowieConfig />
          <KustomerConfig />
          <ZendeskConfig />
        </IntegrationSection>

        <IntegrationSection
          title="Email & SMS Providers"
          id="email-sms-providers"
        >
          <KlaviyoConfig />
          <SendlaneConfig />
          <AttentiveConfig />
          <PostscriptConfig />
        </IntegrationSection>

        <IntegrationSection
          title="Subscription Management"
          id="subscription-management"
        >
          <SkioConfig />
          <RechargeConfig />
        </IntegrationSection>

        <IntegrationSection title="Other Integrations" id="other-integrations">
          <StripeConfig />

          <LocalizeConfig />
          <BrijConfig />
          <IntelligemsConfig />
        </IntegrationSection>
      </div>
    </Page>
  );
}
